import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  FileTextIcon,
  SaveIcon,
  SendIcon
} from 'lucide-react';
import { AppBar } from '../../components/AppBar';
import { ProposalReadView } from '../../components/ProposalReadView';
import { usePortal } from '../../contexts/PortalContext';
import * as api from '../../api/research';
import type { CommitteeProposalDetail } from '../../api/research';
import {
  ANSWER_BLANK,
  ANSWER_NA,
  ANSWER_NO,
  ANSWER_YES,
  type ReviewQuestionRow,
  type ReviewSectionRow
} from '../../api/types';

const CHOICES: { value: number; label: string }[] = [
  { value: ANSWER_YES, label: 'Yes' },
  { value: ANSWER_NO, label: 'No' },
  { value: ANSWER_NA, label: 'N/A' }
];

export function ReviewForm() {
  const { researchCode: codeParam } = useParams();
  const researchCode = Number(codeParam);
  const { sessionToken } = usePortal();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<CommitteeProposalDetail | null>(null);
  const [sections, setSections] = useState<ReviewSectionRow[]>([]);
  const [questions, setQuestions] = useState<ReviewQuestionRow[]>([]);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [canEdit, setCanEdit] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showUnanswered, setShowUnanswered] = useState(false);

  const load = useCallback(async () => {
    if (!sessionToken || !researchCode) return;
    setLoading(true);
    setError(null);
    try {
      const [proposal, review] = await Promise.all([
        api.committeeProposal(sessionToken, researchCode),
        api.getReview({ SessionToken: sessionToken, ResearchCode: researchCode })
      ]);

      if (!proposal.ok) setError(proposal.message);
      else setDetail(proposal.detail);

      if (!review.ok) {
        setError(review.message);
      } else {
        setSections(review.sections);
        setQuestions(review.questions);
        setCanEdit(review.canEdit);
        setIsReviewed(review.isReviewed);
        setAnswers(
          Object.fromEntries(review.questions.map((q) => [q.ColumnName, Number(q.Answer)]))
        );
        setComments(
          Object.fromEntries(
            review.comments.map((c) => [c.SectionKey, String(c.Comment ?? '')])
          )
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the review.');
    } finally {
      setLoading(false);
    }
  }, [researchCode, sessionToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const unanswered = useMemo(
    () => questions.filter((q) => (answers[q.ColumnName] ?? ANSWER_BLANK) === ANSWER_BLANK),
    [answers, questions]
  );

  const persist = async (submit: boolean) => {
    if (!sessionToken) return;

    if (submit && unanswered.length > 0) {
      setShowUnanswered(true);
      setError(
        `${unanswered.length} question${unanswered.length === 1 ? ' is' : 's are'} still unanswered.`
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.saveReview({
        SessionToken: sessionToken,
        ResearchCode: researchCode,
        Answers: JSON.stringify(answers),
        Comments: JSON.stringify(comments),
        Submit: submit
      });

      if (!res.ok) {
        setError(res.message);
        return;
      }

      setNotice(
        res.allReviewsIn
          ? `${res.message} Every assigned review is now in, so the proposal has moved to the IRB office.`
          : res.message
      );
      if (submit) {
        setIsReviewed(true);
        setCanEdit(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the review.');
    } finally {
      setBusy(false);
    }
  };

  if (!sessionToken) return null;

  const header = detail?.header;

  return (
    <div className="page">
      <AppBar showHome={false} />
      <main className="container section-gap">
        <div className="stack">
          <button type="button" className="link-button" onClick={() => navigate('/reviewer')}>
            <ArrowLeftIcon size={14} aria-hidden="true" /> Back to my reviews
          </button>

          {error ? (
            <div className="notice notice--error" role="alert">
              <AlertCircleIcon size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          {notice ? (
            <div className="notice" role="status">
              <CheckCircle2Icon size={18} aria-hidden="true" />
              <span>{notice}</span>
            </div>
          ) : null}

          {loading ? (
            <p className="table-empty">Loading…</p>
          ) : (
            <>
              <section className="hero">
                <div>
                  <h1>{detail?.body?.Title || 'Untitled proposal'}</h1>
                  <p>
                    {header?.IrbNumber ?? `#${researchCode}`} ·{' '}
                    {detail?.body?.ReviewCategory ?? 'Review category not set'}
                  </p>
                </div>
                <div className="inline-actions" style={{ alignItems: 'center' }}>
                  {header?.FileName ? (
                    <a
                      className="btn btn--ghost"
                      href={api.submissionPdfUrl(sessionToken, researchCode)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FileTextIcon size={16} aria-hidden="true" />
                      Submitted document
                    </a>
                  ) : null}
                  <span className={`badge ${isReviewed ? 'badge--green' : 'badge--grey'}`}>
                    {isReviewed ? 'Review submitted' : 'Review in progress'}
                  </span>
                </div>
              </section>

              {isReviewed ? (
                <div className="notice" role="status">
                  <CheckCircle2Icon size={18} aria-hidden="true" />
                  <span>
                    You have already submitted this review, so it is now read-only. Contact
                    the IRB office if something needs changing.
                  </span>
                </div>
              ) : null}

              {detail ? (
                <details className="proposal-details">
                  <summary>Read the full proposal</summary>
                  <div style={{ marginTop: 'var(--space-4)' }}>
                    <ProposalReadView
                      body={detail.body}
                      typeNames={detail.typeNames}
                      gantt={detail.gantt}
                      submission={detail.submission}
                      coInvestigators={detail.coInvestigators}
                      documents={detail.documents}
                      sessionToken={sessionToken}
                      researchCode={researchCode}
                    />
                  </div>
                </details>
              ) : null}

              {sections.map((section) => {
                const sectionQuestions = questions.filter(
                  (q) => q.SectionKey === section.SectionKey
                );
                if (sectionQuestions.length === 0) return null;

                return (
                  <section className="card" key={section.SectionKey}>
                    <div className="card-header">
                      <h2 className="card-title">{section.SectionName}</h2>
                    </div>
                    <div className="card-body">
                      <ul className="question-list">
                        {sectionQuestions.map((q) => {
                          const value = answers[q.ColumnName] ?? ANSWER_BLANK;
                          const flagged = showUnanswered && value === ANSWER_BLANK;
                          return (
                            <li
                              key={q.ColumnName}
                              className={`question${flagged ? ' question--missing' : ''}`}
                            >
                              <span className="question-text" id={`q-${q.ColumnName}`}>
                                {q.QuestionText}
                              </span>
                              <div
                                className="choice-row"
                                role="radiogroup"
                                aria-labelledby={`q-${q.ColumnName}`}
                              >
                                {CHOICES.map((choice) => (
                                  <label
                                    key={choice.value}
                                    className={`choice${
                                      value === choice.value ? ' choice--active' : ''
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name={q.ColumnName}
                                      value={choice.value}
                                      disabled={!canEdit}
                                      checked={value === choice.value}
                                      onChange={() =>
                                        setAnswers((prev) => ({
                                          ...prev,
                                          [q.ColumnName]: choice.value
                                        }))
                                      }
                                    />
                                    {choice.label}
                                  </label>
                                ))}
                              </div>
                            </li>
                          );
                        })}
                      </ul>

                      <div className="field" style={{ marginTop: 'var(--space-4)' }}>
                        <label className="field-label" htmlFor={`comment-${section.SectionKey}`}>
                          Comments on {section.SectionName.toLowerCase()} (if any)
                        </label>
                        <textarea
                          id={`comment-${section.SectionKey}`}
                          className="textarea"
                          rows={4}
                          disabled={!canEdit}
                          value={comments[section.CommentColumn] ?? ''}
                          onChange={(e) =>
                            setComments((prev) => ({
                              ...prev,
                              [section.CommentColumn]: e.target.value
                            }))
                          }
                        />
                      </div>
                    </div>
                  </section>
                );
              })}

              {canEdit ? (
                <section className="card">
                  <div className="card-body submit-bar">
                    <div>
                      <h2 className="card-title">Finished?</h2>
                      <p className="card-subtitle">
                        Save as often as you like. Submitting locks your answers and tells
                        the IRB office you are done — when every assigned review is in, the
                        proposal moves on automatically.
                        {unanswered.length > 0
                          ? ` ${unanswered.length} question${
                              unanswered.length === 1 ? '' : 's'
                            } still unanswered.`
                          : ' All questions answered.'}
                      </p>
                    </div>
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="btn btn--secondary"
                        disabled={busy}
                        onClick={() => void persist(false)}
                      >
                        <SaveIcon size={16} aria-hidden="true" />
                        Save draft
                      </button>
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={busy}
                        onClick={() => void persist(true)}
                      >
                        <SendIcon size={16} aria-hidden="true" />
                        {busy ? 'Submitting…' : 'Submit review'}
                      </button>
                    </div>
                  </div>
                </section>
              ) : null}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
